import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { humanise } from "@/lib/dealStages";
import { formatDate, formatMoneyCents } from "@/lib/format";
import { ENGAGEMENT_SERVICES } from "@/lib/records";
import { useToast } from "./Toast";
import { Badge, Button, Card, Empty, Field, inputClass } from "./ui";

/**
 * The lead page's Engagements tab (v2.1 T6.2, D-56).
 *
 * A survey or a title search is often ordered while the land is still a lead — before anyone would call
 * it a deal. Those engagements hang off the lead (lead_id set, deal_id null); the engagement board,
 * Today and Analytics' fulfilment already count every engagement, and promoting the lead moves them to
 * the deal, where their paid amounts are part of sunk DD cost.
 *
 * The RFQ is drafted, never sent (rule 1): the engagement starts at `rfq_draft`, as on a deal.
 */
export function LeadEngagements({ leadId }: { leadId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: rows } = useQuery({
    queryKey: ["lead-engagements", leadId],
    queryFn: () => api.leadEngagements(leadId),
  });
  const { data: vendors } = useQuery({
    queryKey: ["vendors", "active-for-engagement"],
    queryFn: () => api.vendors({ active: true }),
  });
  const [adding, setAdding] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [service, setService] = useState<string>("boundary_survey");
  const [due, setDue] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.createEngagement({
        vendor_id: vendorId,
        service: service,
        lead_id: leadId,
        due_at: due ? new Date(`${due}T17:00:00`).toISOString() : null,
      }),
    onSuccess: () => {
      toast.push("Engagement added — the RFQ is drafted, not sent");
      setAdding(false);
      setVendorId("");
      void qc.invalidateQueries({ queryKey: ["lead-engagements", leadId] });
      void qc.invalidateQueries({ queryKey: ["engagements"] });
      void qc.invalidateQueries({ queryKey: ["overview"] });
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <Card
      title={`Engagements (${rows?.length ?? 0})`}
      data-testid="lead-engagements"
      action={
        <Button size="sm" data-testid="lead-engagement-add" onClick={() => setAdding((a) => !a)}>
          <Plus className="size-3.5" aria-hidden /> Add engagement
        </Button>
      }
    >
      {adding && (
        <div className="mb-3 grid grid-cols-1 gap-2 rounded border p-2 sm:grid-cols-3" data-testid="lead-engagement-form">
          <Field label="Vendor" required>
            <select
              value={vendorId}
              className={inputClass}
              data-testid="lead-engagement-vendor"
              onChange={(e) => setVendorId(e.target.value)}
            >
              <option value="">Pick a vendor</option>
              {(vendors ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} · {humanise(v.type)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Service" required>
            <select
              value={service}
              className={inputClass}
              data-testid="lead-engagement-service"
              onChange={(e) => setService(e.target.value)}
            >
              {ENGAGEMENT_SERVICES.map((s) => (
                <option key={s} value={s}>
                  {humanise(s)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Due">
            <input
              type="date"
              value={due}
              className={inputClass}
              data-testid="lead-engagement-due"
              onChange={(e) => setDue(e.target.value)}
            />
          </Field>
          <div className="sm:col-span-3 flex justify-end">
            <Button
              size="sm"
              variant="primary"
              disabled={!vendorId || create.isPending}
              data-testid="lead-engagement-save"
              onClick={() => create.mutate()}
            >
              Create (RFQ drafted, not sent)
            </Button>
          </div>
        </div>
      )}
      {(rows ?? []).length === 0 && !adding ? (
        <Empty
          title="No engagements on this lead."
          hint="A survey or title search ordered before the deal exists belongs here; promoting the lead takes it to the deal."
        />
      ) : (
        <ul className="space-y-1 text-xs" data-testid="lead-engagement-list">
          {(rows ?? []).map((e) => (
            <li key={e.id} className="flex flex-wrap items-center gap-2 rounded border px-2 py-1.5" data-testid="lead-engagement">
              <span className="font-medium">{humanise(e.service)}</span>
              <span className="text-fg-muted">{e.vendor_name}</span>
              <Badge tone={e.overdue ? "danger" : "muted"}>{humanise(e.status)}</Badge>
              <span className="ml-auto tabular-nums text-fg-muted">
                {e.due_at ? `due ${formatDate(e.due_at)}` : "no due date"} · paid{" "}
                {formatMoneyCents(e.paid_cents)}
              </span>
              <Link to="/engagements" className="text-primary hover:underline">
                board
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
